import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Phone, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const ServicesDropdown = () => {
  const [activeCategory, setActiveCategory] = useState('trademark');
  const [isOpen, setIsOpen] = useState(false);
  
  const serviceCategories = {
    trademark: {
      title: 'Trademark Services',
      icon: '🏷️',
      description: 'Comprehensive trademark protection for your brand',
      services: [
        { name: 'Trademark Search & Registration', desc: 'Protect your brand name and logo', link: '/services/trademark/search-registration' },
        { name: 'Trademark Clearance Searches', desc: 'Verify availability before filing', link: '/services/trademark/clearance' },
        { name: 'Trademark Application Preparation', desc: 'Professional USPTO applications', link: '/services/trademark/application' },
        { name: 'Trademark Office Actions', desc: 'Respond to USPTO objections', link: '/services/trademark/office-actions' },
        { name: 'Trademark Monitoring', desc: 'Track potential infringements', link: '/services/trademark/monitoring' },
        { name: 'Statement Of Use Filings', desc: 'Complete your ITU application', link: '/services/trademark/statement-of-use' },
        { name: 'Trademark Change of Ownership', desc: 'Transfer trademark rights', link: '/services/trademark/ownership-change' },
        { name: 'Trademark Portfolio Management', desc: 'Manage multiple trademarks', link: '/services/trademark/portfolio' }
      ]
    },
    copyright: {
      title: 'Copyright Services',
      icon: '©️',
      description: 'Protect your creative works and intellectual content',
      services: [
        { name: 'Copyright Registration', desc: 'Official U.S. Copyright Office filing', link: '/services/copyright/registration' },
        { name: 'Copyright Enforcement', desc: 'Stop unauthorized use', link: '/services/copyright/enforcement' },
        { name: 'Copyright Assignments & Licensing', desc: 'Transfer and license rights', link: '/services/copyright/assignments' },
        { name: 'Copyright Litigation', desc: 'Federal court representation', link: '/services/copyright/litigation' },
        { name: 'Copyright Portfolio Management', desc: 'Manage creative assets', link: '/services/copyright/portfolio' },
        { name: 'Digital Millennium Copyright Act', desc: 'DMCA takedown services', link: '/services/copyright/dmca' }
      ]
    },
    patent: {
      title: 'Patent Services',
      icon: '💡',
      description: 'Secure your innovations and inventions',
      services: [
        { name: 'Patent Searches', desc: 'Prior art and patentability analysis', link: '/services/patent/searches' },
        { name: 'Office Actions & Amendments', desc: 'Overcome USPTO rejections', link: '/services/patent/office-actions' },
        { name: 'Patent Applications', desc: 'Utility, design, and provisional patents', link: '/services/patent/applications' },
        { name: 'Patent Enforcement', desc: 'Protect against infringement', link: '/services/patent/enforcement' }
      ]
    },
    company: {
      title: 'Company Formation',
      icon: '🏢',
      description: 'Establish your business structure properly',
      services: [
        { name: 'LLC Formation', desc: 'Limited liability protection', link: '/services/company/llc' },
        { name: 'C Corporation', desc: 'For growth and investment', link: '/services/company/c-corp' },
        { name: 'S Corporation', desc: 'Pass-through taxation benefits', link: '/services/company/s-corp' },
        { name: 'Non-Profit Organization', desc: 'Tax-exempt entity formation', link: '/services/company/non-profit' }
      ]
    }
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        className="flex items-center text-white hover:text-[#F97316] transition-colors font-medium"
      >
        Services
        <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div 
            className="fixed left-0 right-0 top-16 bg-white shadow-2xl z-50 border-t-4 border-[#012169]"
            onMouseLeave={() => setIsOpen(false)}
          >
            <div className="max-w-7xl mx-auto flex">
          {/* Left Sidebar - Categories with Icons */}
          <div className="w-80 bg-gradient-to-br from-[#012169] to-[#013d9e] p-8">
            <div className="mb-8">
              <h3 className="text-[#F97316] font-bold text-base uppercase tracking-wide mb-2" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Our Services</h3>
              <div className="w-16 h-1 bg-[#F97316] rounded-full"></div>
            </div>
            <div className="space-y-3">
              {Object.entries(serviceCategories).map(([key, category]) => (
                <button
                  key={key}
                  onMouseEnter={() => setActiveCategory(key)}
                  className={`w-full text-left px-4 py-4 rounded-xl transition-all duration-300 flex items-center gap-3 ${
                    activeCategory === key 
                      ? 'bg-[#F97316] text-white font-bold shadow-lg scale-105' 
                      : 'text-white hover:bg-white/10 hover:translate-x-1'
                  }`}
                >
                  <span className="text-2xl">{category.icon}</span>
                  <div className="flex-1">
                    <span className="text-base block">{category.title}</span>
                  </div>
                  {activeCategory === key && <ChevronRight className="h-5 w-5" />}
                </button>
              ))}
            </div>
            
            {/* Bottom decoration */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-white/80 text-xs leading-relaxed">
                💼 Need expert guidance? Our IP attorneys are ready to help.
              </p>
            </div>
          </div>

          {/* Right Content - Services List with Descriptions */}
          <div className="flex-1 p-10 bg-gradient-to-br from-gray-50 to-white">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl">{serviceCategories[activeCategory].icon}</span>
                <h2 className="text-3xl font-bold text-[#012169]">
                  {serviceCategories[activeCategory].title}
                </h2>
              </div>
              <p className="text-gray-600 text-lg leading-relaxed">
                {serviceCategories[activeCategory].description}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {serviceCategories[activeCategory].services.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  onClick={() => setIsOpen(false)}
                  className="flex items-start gap-3 p-5 rounded-xl bg-white border-2 border-gray-100 hover:border-[#F97316] hover:shadow-lg transition-all duration-300 group/item"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-[#012169] to-[#8B5CF6] rounded-lg flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform duration-300">
                    <ChevronRight className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-gray-800 group-hover/item:text-[#012169] mb-1 leading-snug">
                      {service.name}
                    </h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {service.desc}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

const Navbar = ({ onOpenModal }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 shadow-lg">
      {/* Top Bar - Orange */}
      <div className="bg-[#F97316] py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center">
            <div className="flex items-center gap-2 sm:gap-4">
              <a href="tel:+18882032966" className="flex items-center gap-1 sm:gap-2 text-white hover:text-[#012169] transition-colors">
                <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="font-semibold text-xs sm:text-sm hidden sm:inline">+1 (888) 203-2966</span>
                <span className="font-semibold text-xs sm:hidden">Call</span>
              </a>
              <button 
                onClick={() => {
                  // This will trigger the Tawk.to chat widget if it exists
                  if (window.Tawk_API) {
                    window.Tawk_API.toggle();
                  }
                }}
                className="flex items-center gap-1 sm:gap-2 bg-white text-[#F97316] hover:bg-[#012169] hover:text-white px-2 sm:px-4 py-1 sm:py-1.5 rounded-md transition-all font-semibold text-xs sm:text-sm"
              >
                <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Chat</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar - Navy Blue */}
      <div className="bg-[#012169]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/assets/img/brand/logo-light.png" 
                alt="Crystal Trademark" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-white hover:text-[#F97316] transition-colors font-medium ${
                  isActive('/') ? 'text-[#F97316]' : ''
                }`}
              >
                Home
              </Link>
              
              <Link
                to="/about"
                className={`text-white hover:text-[#F97316] transition-colors font-medium ${
                  isActive('/about') ? 'text-[#F97316]' : ''
                }`}
              >
                About Us
              </Link>
              
              <ServicesDropdown />
              
              <Link
                to="/contact"
                className={`text-white hover:text-[#F97316] transition-colors font-medium ${
                  isActive('/contact') ? 'text-[#F97316]' : ''
                }`}
              >
                Contact Us
              </Link>
            </div>

            {/* CTA Button - Right */}
            <div className="hidden md:block">
              <Button
                onClick={() => onOpenModal && onOpenModal('Navbar CTA')}
                className="bg-[#F97316] hover:bg-[#e5a200] text-white font-bold px-8 py-3 rounded-md"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white hover:text-[#F97316]"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-4">
                <Link to="/" className="text-white hover:text-[#F97316] transition-colors">
                  Home
                </Link>
                <Link to="/about" className="text-white hover:text-[#F97316] transition-colors">
                  About Us
                </Link>
                <Link to="/contact" className="text-white hover:text-[#F97316] transition-colors">
                  Contact Us
                </Link>
                <Button
                  onClick={() => onOpenModal && onOpenModal('Navbar CTA Mobile')}
                  className="bg-[#F97316] hover:bg-[#e5a200] text-white font-bold w-full"
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;